import React, { ReactNode, useCallback, useState } from 'react';

import { nextAsyncQueue } from '@vimeo/async/wait';
import {
  rem,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  Paragraph,
  useColorModeValue,
  SystemStyleObject,
  Box,
} from '@vimeo/bokeh';
import { IBaseDomElementProps } from '@vimeo/core/types/dom';
import { useScrollbarStyles } from '@vimeo/live-core/hooks/styling/useScrollbarStyles';
import { EInteractionAuthOption } from '@vimeo/live-core/types/interaction_room';
import { LoginContainer } from '@vimeo/ui/core/form/LoginJoinModal';
import { Viewer, useViewer } from '@vimeo/viewer';

import { createInteractionDomName } from '../../../../../../libs/shared/utils/dom/identifying';
import { translations } from '../../../../../../translations';
import { useInteractionLoginOption } from '../shared/useInteractionLoginOption';

type TLoginType = 'login' | 'join';

interface ILoginButtonProps {
  id: string;
  label: string;
  isDisabled: boolean;
  onClick: () => void;
}

interface IVimeoUserLoginProps extends IBaseDomElementProps {
  buttonRenderer: (props: ILoginButtonProps) => ReactNode;
  loginType?: EInteractionAuthOption;
}

/**
 * Generic handler of vimeo login with modal forms.
 */
export function VimeoUserLogin({
  id = createInteractionDomName('vimeo-user-login'),
  loginType = EInteractionAuthOption.VIMEO,
  buttonRenderer,
}: IVimeoUserLoginProps) {
  const [isActive, setActive] = useState<boolean>(false);
  const [type, setType] = useState<TLoginType>('login');

  const viewer: Viewer = useViewer();
  const { isLoading, isLoginFailed, errorMessage, onLogin } = useInteractionLoginOption(loginType);

  const vimeoLoginRequired: boolean = Boolean(viewer && !viewer?.user);

  const onAuthClicked = useCallback(() => onLogin(), [onLogin]);

  const onModalClose = useCallback(() => setActive(false), []);

  const onModalOpen = useCallback(() => setActive(true), []);

  const switchType = useCallback(
    () => setType((prev) => (prev === 'login' ? 'join' : 'login')),
    [],
  );

  const onVimeoLoginSuccess = useCallback(async () => {
    if (viewer.refreshJwt) {
      await viewer.refreshJwt();

      await nextAsyncQueue();

      await onLogin();
    }
  }, [viewer, onLogin]);

  const modalContentBackgroundColor = useColorModeValue('white', 'grayscale.800');
  const modalContentColor = useColorModeValue('text-primary', 'white');

  const scrollbarStyles: SystemStyleObject = useScrollbarStyles({
    width: rem(8),
    scrollbarColor: 'transparent',
  });

  return (
    <>
      {vimeoLoginRequired ? (
        <Modal isOpen={isActive} onClose={onModalClose}>
          <ModalOverlay zIndex={1} />

          <ModalContent
            padding={rem(20)}
            maxWidth={rem(440)}
            color={modalContentColor}
            backgroundColor={modalContentBackgroundColor}
            sx={{
              p: {
                color: modalContentColor,
              },

              ['@media (orientation: landscape)']: {
                maxHeight: '90vh',
              },

              [`@media (min-width: ${rem(480)})`]: {
                height: 'auto',
              },
            }}
          >
            <Box
              sx={{
                ...scrollbarStyles,
                maxHeight: '100%',
                overflowY: 'auto',
                marginY: rem(35),
                marginX: rem(-16),
              }}
            >
              <ModalHeader
                fontSize={rem(18)}
                textAlign={'center'}
                marginBottom={rem(12)}
                padding={0}
              >
                {type === 'login' ? translations.loginToVimeo : translations.joinVimeo}
              </ModalHeader>

              <ModalCloseButton />

              <LoginContainer
                xsrft={viewer.xsrft}
                type={type}
                switchType={switchType}
                onSuccess={onVimeoLoginSuccess}
                redirectUrl={window.location.href}
                redirectUrlAfterSocialLogin={window.location.href}
              />
            </Box>
          </ModalContent>
        </Modal>
      ) : null}

      {buttonRenderer({
        id: createInteractionDomName(id, 'button'),
        label: translations.loginWithVimeo,
        isDisabled: isLoading,
        onClick: vimeoLoginRequired ? onModalOpen : onAuthClicked,
      })}

      {isLoginFailed ? (
        <Paragraph id={createInteractionDomName(id, 'error')} size={'xs'} color={'red.500'}>
          {errorMessage}
        </Paragraph>
      ) : null}
    </>
  );
}
